import { supabase } from '../lib/supabase';

// ─── Constants ────────────────────────────────────────────────────────────────
export const OPERATION_TYPES = [
  { value: 'livraison_barriques', label: 'Livraison barriques' },
  { value: 'enlevement_colis', label: 'Enlèvement colis' },
  { value: 'groupage_partenaire', label: 'Groupage partenaire' },
  { value: 'depot_colis_conteneur', label: 'Dépôt colis conteneur partenaire' },
  { value: 'autre', label: 'Autre' },
];

export const REVERSEMENT_STATUSES = [
  { value: 'a_reverser', label: 'À reverser', color: 'red' },
  { value: 'partiellement_reverse', label: 'Partiellement reversé', color: 'orange' },
  { value: 'solde', label: 'Soldé', color: 'green' },
];

export const ENCAISSEMENT_TYPES = [
  { value: 'adt', label: 'ADT encaisse directement' },
  { value: 'prestataire', label: 'Le prestataire encaisse' },
  { value: 'partage', label: 'Paiement partagé' },
  { value: 'non_paye', label: 'Non payé' },
];

export const PAYMENT_MODES = [
  { value: 'especes', label: 'Espèces' },
  { value: 'virement', label: 'Virement' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'autre', label: 'Autre' },
];

export const GLOBAL_STATUSES = [
  { value: 'a_jour', label: 'À jour', color: 'green' },
  { value: 'dette_en_cours', label: 'Dette en cours', color: 'orange' },
  { value: 'retard', label: 'Retard', color: 'red' },
];

// ─── Partner Operations ───────────────────────────────────────────────────────
export const partnerAccountingService = {

  // ── Operations ──────────────────────────────────────────────────────────────
  async getOperations({ providerId, status, limit = 100 } = {}) {
    let query = supabase
      ?.from('partner_operations')
      ?.select('*')
      ?.order('operation_date', { ascending: false })
      ?.limit(limit);

    if (providerId) query = query?.eq('provider_id', providerId);
    if (status) query = query?.eq('reversement_status', status);

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(mapOperation);
  },

  async getOperationById(id) {
    const { data, error } = await supabase
      ?.from('partner_operations')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    if (error) throw error;
    return mapOperation(data);
  },

  async createOperation(opData) {
    const payload = buildOperationPayload(opData);
    const { data, error } = await supabase
      ?.from('partner_operations')
      ?.insert(payload)
      ?.select()
      ?.single();
    if (error) throw error;
    return mapOperation(data);
  },

  async updateOperation(id, opData) {
    const payload = buildOperationPayload(opData);
    const { data, error } = await supabase
      ?.from('partner_operations')
      ?.update({ ...payload, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return mapOperation(data);
  },

  async markOperationAsSolde(id) {
    const { data, error } = await supabase
      ?.from('partner_operations')
      ?.update({
        reversement_status: 'solde',
        amount_reversed_to_adt: supabase?.rpc ? undefined : undefined,
        updated_at: new Date()?.toISOString(),
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return mapOperation(data);
  },

  async deleteOperation(id) {
    const { error } = await supabase
      ?.from('partner_operations')
      ?.delete()
      ?.eq('id', id);
    if (error) throw error;
  },

  // ── Reversements ────────────────────────────────────────────────────────────
  async getReversements({ providerId } = {}) {
    let query = supabase
      ?.from('partner_reversements')
      ?.select('*')
      ?.order('reversal_date', { ascending: false });

    if (providerId) query = query?.eq('provider_id', providerId);

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(mapReversement);
  },

  async createReversement(revData) {
    const payload = {
      provider_id: revData?.providerId || null,
      provider_name: revData?.providerName || '',
      amount_reversed: Number(revData?.amountReversed || 0),
      reversal_date: revData?.reversalDate || new Date()?.toISOString()?.split('T')?.[0],
      payment_mode: revData?.paymentMode || 'especes',
      notes: revData?.notes || null,
    };

    const { data, error } = await supabase
      ?.from('partner_reversements')
      ?.insert(payload)
      ?.select()
      ?.single();
    if (error) throw error;

    // Auto-apply to oldest unpaid operations
    if (revData?.providerId) {
      await supabase?.rpc('apply_reversement_to_operations', {
        p_reversement_id: data?.id,
        p_provider_id: revData?.providerId,
        p_amount: Number(revData?.amountReversed || 0),
      });
    }

    return mapReversement(data);
  },

  async getReversementAllocations(reversementId) {
    const { data, error } = await supabase
      ?.from('partner_reversement_allocations')
      ?.select('*, partner_operations(*)')
      ?.eq('reversement_id', reversementId);
    if (error) throw error;
    return data || [];
  },

  // ── Summary Functions ────────────────────────────────────────────────────────
  async getGlobalSummary() {
    const { data, error } = await supabase?.rpc('get_partner_accounting_summary');
    if (error) throw error;
    return data?.[0] || {
      total_collected_by_partners: 0,
      total_partner_shares: 0,
      total_due_to_adt: 0,
      total_reversed_to_adt: 0,
      total_remaining_balance: 0,
      total_operations: 0,
      overdue_operations: 0,
    };
  },

  async getSummaryByProvider() {
    const { data, error } = await supabase?.rpc('get_partner_summary_by_provider');
    if (error) throw error;
    return (data || [])?.map(row => ({
      providerId: row?.provider_id,
      providerName: row?.provider_name,
      operationCount: Number(row?.operation_count || 0),
      totalCollected: Number(row?.total_collected || 0),
      totalPartnerShare: Number(row?.total_partner_share || 0),
      totalDueToAdt: Number(row?.total_due_to_adt || 0),
      totalReversed: Number(row?.total_reversed || 0),
      remainingBalance: Number(row?.remaining_balance || 0),
      globalStatus: row?.global_status || 'a_jour',
    }));
  },

  // ── Alerts ──────────────────────────────────────────────────────────────────
  async getOverdueOperations(daysThreshold = 3) {
    const cutoffDate = new Date();
    cutoffDate?.setDate(cutoffDate?.getDate() - daysThreshold);

    const { data, error } = await supabase
      ?.from('partner_operations')
      ?.select('*')
      ?.neq('reversement_status', 'solde')
      ?.lt('operation_date', cutoffDate?.toISOString()?.split('T')?.[0])
      ?.order('operation_date', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(mapOperation);
  },
};

// ─── Mappers ──────────────────────────────────────────────────────────────────
function mapOperation(row) {
  if (!row) return null;
  return {
    id: row?.id,
    providerId: row?.provider_id,
    providerName: row?.provider_name,
    clientName: row?.client_name,
    clientReference: row?.client_reference,
    shipmentReference: row?.shipment_reference,
    promoOrderId: row?.promo_order_id,
    operationType: row?.operation_type,
    operationDate: row?.operation_date,
    amountCollectedByPartner: Number(row?.amount_collected_by_partner || 0),
    partnerShare: Number(row?.partner_share || 0),
    amountDueToAdt: Number(row?.amount_due_to_adt || 0),
    amountReversedToAdt: Number(row?.amount_reversed_to_adt || 0),
    remainingBalance: Number(row?.remaining_balance || 0),
    reversementStatus: row?.reversement_status,
    encaissementType: row?.encaissement_type,
    notes: row?.notes,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
  };
}

function mapReversement(row) {
  if (!row) return null;
  return {
    id: row?.id,
    providerId: row?.provider_id,
    providerName: row?.provider_name,
    amountReversed: Number(row?.amount_reversed || 0),
    reversalDate: row?.reversal_date,
    paymentMode: row?.payment_mode,
    notes: row?.notes,
    createdAt: row?.created_at,
  };
}

function buildOperationPayload(data) {
  return {
    provider_id: data?.providerId || null,
    provider_name: data?.providerName || '',
    client_name: data?.clientName || null,
    client_reference: data?.clientReference || null,
    shipment_reference: data?.shipmentReference || null,
    promo_order_id: data?.promoOrderId || null,
    operation_type: data?.operationType || 'autre',
    operation_date: data?.operationDate || new Date()?.toISOString()?.split('T')?.[0],
    amount_collected_by_partner: Number(data?.amountCollectedByPartner || 0),
    partner_share: Number(data?.partnerShare || 0),
    amount_due_to_adt: Number(data?.amountDueToAdt || 0),
    amount_reversed_to_adt: Number(data?.amountReversedToAdt || 0),
    reversement_status: data?.reversementStatus || 'a_reverser',
    encaissement_type: data?.encaissementType || 'prestataire',
    notes: data?.notes || null,
  };
}
